import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timeout, catchError, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';

export type FaceFeatureContext = 'face_compare' | 'video_scanner';

@Injectable({
  providedIn: 'root',
})
export class FaceCompareService {
  private apiUrl = environment.baseUrl;
  private timeout = 120;
  private uploadedImagesSubject = new BehaviorSubject<any[]>([]);
  private selectedImagesSubject = new BehaviorSubject<any[]>([]);
  private compareFacesTriggerSubject = new Subject<void>();
  private splitFacesTriggerSubject = new Subject<void>();
  private splitViewDialogTriggerSubject = new Subject<void>();
  private splitViewActiveSubject = new BehaviorSubject<boolean>(false);
  private viewUploadsHistorySubject = new Subject<void>();
  private openHistoryOnNextLoad = false;
  private featureContext: FaceFeatureContext = 'face_compare';

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
  ) {}

  uploadedImages$ = this.uploadedImagesSubject.asObservable();
  selectedImages$ = this.selectedImagesSubject.asObservable();
  compareFacesTrigger$ = this.compareFacesTriggerSubject.asObservable();
  splitFacesTrigger$ = this.splitFacesTriggerSubject.asObservable();
  splitViewDialogTrigger$ = this.splitViewDialogTriggerSubject.asObservable();
  splitViewActive$ = this.splitViewActiveSubject.asObservable();
  viewUploadsHistory$ = this.viewUploadsHistorySubject.asObservable();

  setFeatureContext(context: FaceFeatureContext): void {
    this.featureContext = context;
  }

  getFeatureContext(): FaceFeatureContext {
    return this.featureContext;
  }

  getUploadedImages(): any[] {
    return this.uploadedImagesSubject.value;
  }

  getSelectedImages(): any[] {
    return this.selectedImagesSubject.value;
  }

  updateUploadedImages(images: any[]): void {
    this.uploadedImagesSubject.next(images);
  }

  setSelectedImages(images: any[]): void {
    this.selectedImagesSubject.next(images);
  }

  clearAll(): void {
    this.uploadedImagesSubject.next([]);
    this.selectedImagesSubject.next([]);
    this.splitViewActiveSubject.next(false);
  }

  setSplitViewActive(isActive: boolean): void {
    this.splitViewActiveSubject.next(isActive);
  }

  getSplitViewActive(): boolean {
    return this.splitViewActiveSubject.value;
  }

  triggerFaceComparison(): void {
    this.compareFacesTriggerSubject.next();
  }

  triggerSplitFaces(): void {
    this.splitFacesTriggerSubject.next();
  }

  triggerSplitViewDialog(): void {
    this.splitViewDialogTriggerSubject.next();
  }

  triggerViewUploadsHistory(): void {
    this.openHistoryOnNextLoad = true;
    this.viewUploadsHistorySubject.next();
  }

  consumeOpenHistoryRequest(): boolean {
    const shouldOpen = this.openHistoryOnNextLoad;
    this.openHistoryOnNextLoad = false;
    return shouldOpen;
  }

  private handleTimeout<T>(obs: Observable<T>): Observable<T> {
    return obs.pipe(
      timeout(this.timeout * 1000),
      catchError((err) => {
        if (err.name === 'TimeoutError') {
          this.toastr.error('Request timed out! Please try again.');
        } else if (err?.error?.message_key) {
          this.toastr.error(err.error.message_key);
        } else {
          this.toastr.error('Something went wrong. Please try again.');
        }
        return throwError(() => err);
      }),
    );
  }

  getFacesFromImage(imageBase64: string): Observable<any> {
    return this.handleTimeout(
      this.http.post(`${this.apiUrl}/face/detect`, {
        image: imageBase64,
        feature_context: this.featureContext,
      }),
    );
  }

  getEnhancedImage(imageBase64: string): Observable<any> {
    return this.handleTimeout(
      this.http.post(`${this.apiUrl}/face/enhance`, {
        image: imageBase64,
        feature_context: this.featureContext,
      }),
    );
  }

  getFaceComparisonResults(payload: any): Observable<any> {
    return this.handleTimeout(
      this.http.post(`${this.apiUrl}/face/compare`, {
        ...payload,
        feature_context: 'face_compare',
      }),
    );
  }
}
